import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { utils, writeFile } from 'xlsx';
import { ArrowDownTrayIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const ProspectsReport = () => {
  const [activeTab, setActiveTab] = useState('todas'); // Default tab
  const [processedData, setProcessedData] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59, 999));

  const [showGlobalFilters, setShowGlobalFilters] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const tableHeaders = useMemo(() => [
    'ID', 'Fecha', 'Quote ID', 'Tags', 'Cobertura', 'Marca', 'Submarca', 'Año', 'Handoff'
  ], []);

 const columnsConfig = useMemo(() => ({
  todas: tableHeaders,
  mercately: tableHeaders,
  'en-proceso': tableHeaders,
  'enlace-wa': tableHeaders,
}), [tableHeaders]);


  // States to hold raw and date-range data
  const [conversations, setConversations] = useState([]);
  const [tagsMap, setTagsMap] = useState(new Map());
  const [dateRangeData, setDateRangeData] = useState([]);
  const [reportStats, setReportStats] = useState({
  });
  const [carCounts, setCarCounts] = useState({});

  // Tab-specific states
  const [tabData, setTabData] = useState({
    todas: [],
    mercately: [],
    'en-proceso': [],
    'enlace-wa': [],
  });

  const [tabFilters, setTabFilters] = useState({
    todas: { internalFilter: 'exclude', columnFilters: [], sortConfig: { key: 'ID', direction: 'descending' } },
    mercately: { internalFilter: 'exclude', columnFilters: [], sortConfig: { key: 'ID', direction: 'descending' } },
    'en-proceso': { internalFilter: 'exclude', columnFilters: [], sortConfig: { key: 'ID', direction: 'descending' } },
    'enlace-wa': { internalFilter: 'exclude', columnFilters: [], sortConfig: { key: 'ID', direction: 'descending' } },
  });

  const [tabStats, setTabStats] = useState({});


  // New state for manual WA status
  const [handoffStatus, setHandoffStatus] = useState({});
  // State for the manual update form
  const [selectedIdForUpdate, setSelectedIdForUpdate] = useState('');
  const [newHandoffStatus, setNewHandoffStatus] = useState('');
  // State for conditional updates
  // State for Find ID functionality
  const [showFindId, setShowFindId] = useState(false);
  const [idToFind, setIdToFind] = useState('');
  const [foundInTabs, setFoundInTabs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const conversationsRef = collection(db, 'conversations');
      const tagsRef = collection(db, 'tags');
      const manualDataRef = collection(db, 'prospects-manual-data');

      // Fetch all data concurrently
      const [conversationsSnapshot, tagsSnapshot] = await Promise.all([
        getDocs(conversationsRef),
        getDocs(tagsRef),
      ]);

      setConversations(conversationsSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      const newTagsMap = new Map();
      tagsSnapshot.docs.forEach((doc) => {
        newTagsMap.set(doc.id, doc.data().tags || []);
      });
      setTagsMap(newTagsMap);

      const manualDataSnapshot = await getDocs(manualDataRef);
      const handoffData = {};
      manualDataSnapshot.forEach(doc => { handoffData[doc.id] = doc.data().handoff; });
      setHandoffStatus(handoffData);
    };

    fetchData();
  }, []);

  const handleDownloadCarrosExcel = () => {
    const carData = Object.entries(carCounts).map(([car, count]) => ({
      Carro: car,
      '# Cotizaciones': count,
    }));
    const ws = utils.json_to_sheet(carData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Carros Report');
    writeFile(wb, `Carros_Report_${startDate.toISOString().slice(0, 10)}_to_${endDate.toISOString().slice(0, 10)}.xlsx`);
  };

  useEffect(() => {
    if (conversations.length === 0 || !tagsMap.size) {
      setDateRangeData([]);
      return;
    }

    const startTimestamp = startDate.getTime();
    const endTimestamp = endDate.getTime();

    const parseVehicleInfo = (messages) => {
      const vehicleInfo = {
        marca: 'N/A',
        submarca: 'N/A',
        anio: 'N/A',
      };
      const validationMessage = messages.find(
        (m) => m.role === 'system' && m.content.startsWith('Proceso de validación exitoso para el auto')
      );
      if (validationMessage) {
        const vehicleRegex = /Proceso de validación exitoso para el auto ([\w\s-]+) ([\w\s-]+) (\d{4})/;
        const match = validationMessage.content.match(vehicleRegex);
        if (match) {
          vehicleInfo.marca = match[1].trim();
          vehicleInfo.submarca = match[2].trim();
          vehicleInfo.anio = match[3].trim();
        }
      }
      return vehicleInfo;
    };

    const data = conversations.map(conv => {
      const conversationTimestamp = conv.timestamp.seconds * 1000;
      if (conversationTimestamp < startTimestamp || conversationTimestamp > endTimestamp) {
        return null; // Skip this conversation
      }
      const conversationDate = new Date(conversationTimestamp);

      const tags = tagsMap.get(conv.id) || [];
      const { marca, submarca, anio } = parseVehicleInfo(conv.messages);
      
      let handoff = handoffStatus[conv.id]; // Prefer manual status

      // If not manually set, check for the 'hand-off' tag
      if (handoff === undefined && tags.includes('hand-off')) {
        handoff = 'Sí';
      }

      const record = {
        ID: conv.id_simple || 'N/A',
        Fecha: conversationDate.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }),
        rawDate: conversationDate,
        'Quote ID': conv.id,
        Tags: tags.join(', '),
        Cobertura: 'N/A',
        Marca: marca,
        Submarca: submarca,
        Año: anio,
        'Handoff': handoff ?? '',
      };
      return record;
    }).filter(Boolean).filter(item => !item.Tags.includes('internal'));

    setDateRangeData(data);
  }, [startDate, endDate, conversations, tagsMap, handoffStatus]);


  useEffect(() => {
    const items = dateRangeData ? [...dateRangeData] : [];
    
    const todas = items;
    const mercately = items.filter(item => item.Tags.includes('mercately'));
    const enProceso = items.filter(item => !item.Tags.includes('mercately') && !item.Tags.includes('WA'));
    const enlaceWA = items.filter(item => item.Tags.includes('WA'));
    
    const processTabData = (data, tab) => {
      let tabItems = [...data];
      const { internalFilter: tabInternalFilter, columnFilters: tabColumnFilters, sortConfig: tabSortConfig } = tabFilters[tab];

      if (tabInternalFilter === 'only') {
        tabItems = tabItems.filter(item => item.Tags.includes('internal'));
      } else if (tabInternalFilter === 'exclude') {
        tabItems = tabItems.filter(item => !item.Tags.includes('internal'));
      }

      if (tabColumnFilters.length > 0) {
        tabItems = tabItems.filter(item => {
          return tabColumnFilters.every(filter => {
            if (!filter.column || !filter.value) return true;
            const itemValue = String(item[filter.column] || '').toLowerCase();
            const filterValue = String(filter.value).toLowerCase();
            return itemValue.includes(filterValue);
          });
        });
      }

      if (tabSortConfig.key) {
        tabItems.sort((a, b) => {
          const valA = a[tabSortConfig.key];
          const valB = b[tabSortConfig.key];
          const isInvalid = (val) => val === 'N/A' || val === 'No disponible' || val == null;
          if (isInvalid(valA)) return 1;
          if (isInvalid(valB)) return -1;
          let comparison = 0;
          if (tabSortConfig.key === 'Fecha') {
            comparison = a.rawDate.getTime() - b.rawDate.getTime();
          } else if (typeof valA === 'number' && typeof valB === 'number') {
            comparison = valA - valB;
          } else {
            comparison = String(valA).localeCompare(String(valB), undefined, { numeric: true, sensitivity: 'base' });
          }
          return tabSortConfig.direction === 'descending' ? -comparison : comparison;
        });
      }

      const carCountsData = {};
      tabItems.forEach(item => {
        if (item.Marca !== 'N/A') {
          const carKey = `${item.Marca} ${item.Submarca} ${item.Año}`;
          carCountsData[carKey] = (carCountsData[carKey] || 0) + 1;
        }
      });
      return { processed: tabItems, carCounts: carCountsData };
    };

    // Calculate stats on filtered data
    const stats = { mercately: 0, enProceso: 0, enlaceWA: 0, handoff: 0, carAdded: 0 };
    const carCountsData = {}; // This seems to be global car counts, let's keep it
    items.forEach(item => {
      if (item.Tags.includes('mercately')) stats.mercately++; else if (item.Tags.includes('WA')) stats.enlaceWA++; else stats.enProceso++;
      if (item.Marca !== 'N/A') {
        stats.carAdded++;
        const carKey = `${item.Marca} ${item.Submarca} ${item.Año}`;
        carCountsData[carKey] = (carCountsData[carKey] || 0) + 1; // This populates the global car counts
      }
      if (item.Handoff === 'Sí') stats.handoff++;
    });
    setReportStats(stats);
    setCarCounts(carCountsData);

    const todasProcessed = processTabData(todas, 'todas');
    const mercatelyProcessed = processTabData(mercately, 'mercately');
    const enProcesoProcessed = processTabData(enProceso, 'en-proceso');
    const enlaceWAProcessed = processTabData(enlaceWA, 'enlace-wa');

    setTabData({
        todas: todasProcessed.processed,
        mercately: mercatelyProcessed.processed,
        'en-proceso': enProcesoProcessed.processed,
        'enlace-wa': enlaceWAProcessed.processed,
    });

    setTabStats({
        todas: { total: todasProcessed.processed.length, carCounts: todasProcessed.carCounts },
        mercately: { total: mercatelyProcessed.processed.length, carCounts: mercatelyProcessed.carCounts },
        'en-proceso': { total: enProcesoProcessed.processed.length, carCounts: enProcesoProcessed.carCounts },
        'enlace-wa': { total: enlaceWAProcessed.processed.length, carCounts: enlaceWAProcessed.carCounts },
    });

    setProcessedData(items); // Keep global processed data for combined download

  }, [dateRangeData, tabFilters]);

  const handleDownloadExcel = () => {
    const ws = utils.json_to_sheet(dateRangeData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Prospects Report');
    const start = formatDateForInput(startDate);
    const end = formatDateForInput(endDate);
    writeFile(wb, `Prospects_Report_${start}_to_${end}.xlsx`);
  };

  const handleDownloadTabExcel = (tab) => {
    const dataToDownload = tabData[tab];
    if (dataToDownload.length === 0) {
      alert('No hay datos para descargar.');
      return;
    }
    const ws = utils.json_to_sheet(dataToDownload);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, `Reporte ${tab}`);
    const start = formatDateForInput(startDate);
    const end = formatDateForInput(endDate);
    writeFile(wb, `Reporte_${tab}_${start}_to_${end}.xlsx`);
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    const [year, month, day] = value.split('-').map(Number);
    
    if (name === 'startDate') {
      const newStartDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      setStartDate(newStartDate);
    } else {
      const newEndDate = new Date(year, month - 1, day, 23, 59, 59, 999);
      setEndDate(newEndDate);
    }
  };

  const setDateRangePreset = (preset) => {
    const now = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
        end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
        break;
      case 'week':
        const firstDayOfWeek = new Date(now);
        firstDayOfWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1)); // Adjust for week starting on Monday
        start = new Date(firstDayOfWeek.getFullYear(), firstDayOfWeek.getMonth(), firstDayOfWeek.getDate(), 0, 0, 0, 0);
        const lastDayOfWeek = new Date(start);
        lastDayOfWeek.setDate(start.getDate() + 6);
        end = new Date(lastDayOfWeek.getFullYear(), lastDayOfWeek.getMonth(), lastDayOfWeek.getDate(), 23, 59, 59, 999);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      // ... other presets can be added here
      default: return;
    }
    setStartDate(start);
    setEndDate(end);
  };

  const handleTabFilterChange = useCallback((tab, filterType, value, index, field) => {
    setTabFilters(prev => {
      const newTabFilters = { ...prev[tab] };
      if (filterType === 'columnFilters' && newTabFilters.columnFilters[index]) {
        newTabFilters.columnFilters[index][field] = value;
      } else {
        newTabFilters[filterType] = value;
      }
      return { ...prev, [tab]: newTabFilters };
    });
  }, []);
  const handleHandoffUpdate = async () => {
    if (!selectedIdForUpdate) {
      alert('Por favor seleccione un ID.');
      return;
    }

    const conversationToUpdate = dateRangeData.find(p => p.ID.toString() === selectedIdForUpdate.toString());
    if (!conversationToUpdate) {
      alert('ID no encontrado en los datos filtrados.');
      return;
    }
    const quoteId = conversationToUpdate['Quote ID'];

    try {
      const docRef = doc(db, 'prospects-manual-data', quoteId);
      await setDoc(docRef, { handoff: newHandoffStatus }, { merge: true });

      setHandoffStatus(prev => ({ ...prev, [quoteId]: newHandoffStatus }));

      alert(`Estado de Handoff para ID ${selectedIdForUpdate} actualizado a "${newHandoffStatus}".`);
      setSelectedIdForUpdate('');
      setNewHandoffStatus('');
    } catch (error) {
      console.error("Error updating Handoff status: ", error);
      alert('Error al actualizar el estado.');
    }
  };

  const getRowClassName = (row) => {
    const baseClasses = 'border-b border-gray-200';
    if (row['Handoff'] === 'Sí') return `${baseClasses} bg-amber-100 hover:bg-amber-200`;
    if (row.Tags.includes('mercately')) return `${baseClasses} bg-green-100 hover:bg-green-200`;
    if (row.Tags.includes('WA')) return `${baseClasses} bg-blue-100 hover:bg-blue-200`;
    return `${baseClasses} hover:bg-gray-100`; // en-proceso
  };

  const SortableHeader = useCallback(({ columnKey, title }) => {
    const sortConfig = tabFilters[activeTab]?.sortConfig || { key: '', direction: 'ascending' };
    const isSorted = sortConfig.key === columnKey;
    const icon = isSorted ? (
      sortConfig.direction === 'ascending' ? (
        <ChevronUpIcon className="w-4 h-4 ml-1 inline-block shrink-0" />
      ) : (
        <ChevronDownIcon className="w-4 h-4 ml-1 inline-block" />
      )
    ) : null;

    return (
      <th
        className="py-3 px-6 text-left cursor-pointer hover:bg-gray-200"
        onClick={() => {
          setTabFilters(prev => ({
            ...prev,
            [activeTab]: { ...prev[activeTab], sortConfig: { key: columnKey, direction: prev[activeTab].sortConfig.key === columnKey && prev[activeTab].sortConfig.direction === 'ascending' ? 'descending' : 'ascending' } }
          }));
        }}
      >
        <div className="flex items-center">
          {title}
          {icon}
        </div>
      </th>
    );
  }, [activeTab, tabFilters, setTabFilters]);

  const renderTable = (data, tabKey) => {
    const visibleCols = columnsConfig[tabKey];

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
          <thead>
            <tr className="bg-gray-100 text-gray-800 uppercase text-sm leading-normal">
              {visibleCols.map(header => (
                <SortableHeader key={header} columnKey={header} title={header} />
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-700 text-sm font-light">
            {data.map((row, index) => (
              <tr key={`${row['Quote ID']}-${index}`} className={getRowClassName(row)}>
                {visibleCols.map(header => (
                  <td key={header} className="py-3 px-6" title={row[header]}>
                    {
                      (header === 'Quote ID') && row[header] && row[header] !== 'N/A' ? `${row[header].substring(0, 20)}...` : row[header] // Shorten Quote ID
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTabContent = (tabKey) => {
    const data = tabData[tabKey];
    const stats = tabStats[tabKey] || { total: 0, carCounts: {} };

    const tabColorClasses = {
      'todas': 'text-gray-800',
      'en-proceso': 'text-gray-700',
      'enlace-wa': 'text-blue-600',
      'mercately': 'text-green-600',
    };

    return (
      <div className="space-y-4">
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h4 className="font-bold text-lg mb-3 text-gray-700">Filtros de Columna</h4>
          {tabFilters[tabKey] && tabFilters[tabKey].columnFilters.map((filter, index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <select
                value={filter.column}
                onChange={(e) => handleTabFilterChange(tabKey, 'columnFilters', e.target.value, index, 'column')}
                className="p-2 border border-gray-300 rounded-md"
              >
                <option value="">Seleccionar Columna</option>
                {columnsConfig[tabKey] && columnsConfig[tabKey].map(col => <option key={col} value={col}>{col}</option>)}
              </select>
              <input
                type="text"
                placeholder="Valor a filtrar..."
                value={filter.value}
                onChange={(e) => handleTabFilterChange(tabKey, 'columnFilters', e.target.value, index, 'value')}
                className="p-2 border border-gray-300 rounded-md flex-grow"
              />
              <button
                onClick={() => {
                  setTabFilters(prev => ({
                    ...prev,
                    [tabKey]: { ...prev[tabKey], columnFilters: prev[tabKey].columnFilters.filter((_, i) => i !== index) }
                  }));
                }}
                className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              setTabFilters(prev => ({
                ...prev,
                [tabKey]: { ...prev[tabKey], columnFilters: [...prev[tabKey].columnFilters, { column: '', value: '' }] }
              }));
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 mt-2"
          >
            Agregar Filtro
          </button>
        </div>
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h4 className="font-bold text-lg mb-3 text-gray-700">Resumen de {tabKey}</h4>
          <div className="grid grid-cols-2 md:grid-cols-2 gap-4 text-center">
            <div className='grid grid-cols-2'>
              <p className={`text-2xl font-bold ${tabColorClasses[tabKey] || 'text-gray-800'}`}>{stats.total}</p>
              <p className="text-sm text-gray-600 font-medium">Conversaciones Totales</p>
            </div>
            <div className="relative">
              <details className="cursor-pointer group">
                <summary className="text-center list-none">
                  <p className="text-2xl font-bold text-purple-600">{Object.keys(stats.carCounts).length}</p>
                  <p className="text-sm text-gray-600 font-medium flex items-center justify-center">
                    Carros <ChevronDownIcon className="w-4 h-4 ml-1 group-open:rotate-180 transition-transform" />
                  </p>
                </summary>
                <ul className="mt-2 text-left bg-white p-2 rounded shadow-lg max-h-48 overflow-y-auto absolute z-10 border border-gray-200 w-64">
                  {Object.entries(stats.carCounts).length > 0 ? (
                    <>
                      <li className="sticky top-0 bg-white p-1 border-b">
                        <button
                          onClick={handleDownloadCarrosExcel}
                          className="w-full text-sm bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex items-center justify-center"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                          Descargar
                        </button>
                      </li>
                      {Object.entries(stats.carCounts)
                        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                        .map(([car, count]) => (
                          <li key={car} className="text-sm flex justify-between p-1 hover:bg-gray-100">
                            <span>{car}</span>
                            <strong>{count}</strong>
                          </li>
                        ))}
                    </>
                  ) : (
                    <li className="text-sm text-gray-500 p-1">No hay carros con información.</li>
                  )}
                </ul>
              </details>
            </div>
          </div>
        </div>
        <button onClick={() => handleDownloadTabExcel(tabKey)} className="mb-4 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center">
          <ArrowDownTrayIcon className="w-5 h-5" />
          <span className="ml-2">Descargar Excel de {tabKey}</span>
        </button>
        {renderTable(data, tabKey)}
      </div>
    );
  };

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-6xl mx-auto">
      <h3 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">Reportes</h3>

      <div className="flex justify-end mb-4 space-x-2">
        <button
          onClick={() => setShowGlobalFilters(!showGlobalFilters)}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded flex items-center"
        >
          Fecha
        </button>
        <button
          onClick={() => setShowUpdates(!showUpdates)}
          className="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded flex items-center"
        >
          Actualizar
        </button>
        <button
          onClick={() => setShowFindId(true)}
          className="bg-teal-500 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded flex items-center"
        >
          <MagnifyingGlassIcon className="w-5 h-5 mr-2" /> Encontrar ID
        </button>
        <button
          onClick={handleDownloadExcel}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
          disabled={dateRangeData.length === 0}
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
          <span className="ml-2">Descargar Excel</span>
        </button>
      </div>

      {showFindId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Encontrar Prospecto por ID</h3>
            <div className="mt-2">
              <input
                type="text"
                value={idToFind}
                onChange={(e) => {
                  const searchId = e.target.value;
                  setIdToFind(searchId);
                  if (searchId) {
                    const found = [];                    
                    if (tabData.todas.some(d => String(d.ID) === searchId)) found.push('todas');
                    if (tabData['enlace-wa'].some(d => String(d.ID) === searchId)) found.push('enlace-wa');
                    if (tabData.mercately.some(d => String(d.ID) === searchId)) found.push('mercately');                    
                    if (tabData['en-proceso'].some(d => String(d.ID) === searchId)) found.push('en-proceso');
                    setFoundInTabs(found);
                  } else {
                    setFoundInTabs([]);
                  }
                }}
                placeholder="Escriba el ID del prospecto"
                className="w-full p-2 border rounded"
              />
            </div>
            <div className="mt-4">
              {idToFind && (foundInTabs.length > 0 ? (
                <p>ID encontrado en: {foundInTabs.map(t => <button key={t} className="capitalize ml-2 text-blue-600 hover:underline" onClick={() => { setActiveTab(t); setShowFindId(false); }}>
                  {t.replace('en-proceso', 'En Proceso (Clarenta)').replace('enlace-wa', 'Enlace WA').replace('todas', 'Todas')}
                </button>
                )}
                </p>
              ) : <p className="text-red-500">ID no encontrado en ninguna pestaña.</p>)}
            </div>
            <button onClick={() => setShowFindId(false)} className="mt-4 bg-gray-300 hover:bg-gray-400 text-black font-bold py-2 px-4 rounded">Cerrar</button>
          </div>
        </div>
      )}

      {showGlobalFilters && (
        <div className="mb-6 space-y-4">
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="font-bold text-lg mb-3 text-gray-700">Rango de Fechas (Global)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date:</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  className="p-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500"
                  value={formatDateForInput(startDate)}
                  onChange={handleDateChange}
                />
              </div>
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date:</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  className="p-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500"
                  value={formatDateForInput(endDate)}
                  onChange={handleDateChange}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={() => setDateRangePreset('today')} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Hoy</button>
              <button onClick={() => setDateRangePreset('yesterday')} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Ayer</button>
              <button onClick={() => setDateRangePreset('week')} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Semana</button>
              <button onClick={() => setDateRangePreset('month')} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Mes</button>
              <button onClick={() => setDateRangePreset('year')} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Año</button>
            </div>
          </div>
        </div>
      )}

      {showUpdates && (
        <div className="mb-6 space-y-4">
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="font-bold text-lg mb-3 text-gray-700">Actualizar "Handoff"</h4>
            <div className="flex items-center gap-4">
              <div>
                <label htmlFor="handoff-update-id" className="block text-sm font-medium text-gray-700">ID de Conversación</label>
                <select id="handoff-update-id" value={selectedIdForUpdate} onChange={(e) => setSelectedIdForUpdate(e.target.value)} className="p-2 border border-gray-300 rounded-md">
                  <option value="">Seleccionar ID</option>
                  {dateRangeData.slice().sort((a, b) => a.ID - b.ID).map(p => (<option key={p.ID} value={p.ID}>{p.ID}</option>))}
                </select>
              </div>
              <div>
                <label htmlFor="handoff-update-status" className="block text-sm font-medium text-gray-700">Nuevo Estado</label>
                <select id="handoff-update-status" value={newHandoffStatus} onChange={(e) => setNewHandoffStatus(e.target.value)} className="p-2 border border-gray-300 rounded-md">
                  <option value="">Seleccionar Estado</option>
                  <option value="Sí">Sí</option>
                  <option value="No">No</option>
                </select>
              </div>
              <button onClick={handleHandoffUpdate} className="self-end bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600">Guardar</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h4 className="font-bold text-lg mb-3 text-gray-700">Resumen General</h4>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-gray-900">{dateRangeData.length}</p>
            <p className="text-sm text-gray-600 font-medium">
              Total<br/>Conversaciones
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-500">{reportStats.enProceso}</p>
            <p className="text-sm text-gray-600 font-medium">
              En proceso<br/>(Clarenta)
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">{reportStats.enlaceWA}</p>
            <p className="text-sm text-gray-600 font-medium">
              Enlace<br/>WA
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">{reportStats.mercately}</p>
            <p className="text-sm text-gray-600 font-medium">
              Mercately
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-amber-600">{reportStats.handoff}</p>
            <p className="text-sm text-gray-600 font-medium">
              Handoff
            </p>
          </div>
          <div className="relative">
            <details className="cursor-pointer group">
              <summary className="text-center list-none">
                <p className="text-2xl font-bold text-purple-600">{reportStats.carAdded}</p>
                <p className="text-sm text-gray-600 font-medium flex items-center justify-center">
                  Carros <ChevronDownIcon className="w-4 h-4 ml-1 group-open:rotate-180 transition-transform" />
                </p>
              </summary>
              <ul className="mt-2 text-left bg-white p-2 rounded shadow-lg max-h-48 overflow-y-auto absolute z-10 border border-gray-200 w-64">
                {Object.entries(carCounts).length > 0 ? (
                  <>
                    <li className="sticky top-0 bg-white p-1 border-b">
                      <button
                        onClick={handleDownloadCarrosExcel}
                        className="w-full text-sm bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-2 rounded flex items-center justify-center"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                        Descargar
                      </button>
                    </li>
                    {Object.entries(carCounts)
                      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                      .map(([car, count]) => (
                        <li key={car} className="text-sm flex justify-between p-1 hover:bg-gray-100">
                          <span>{car}</span>
                          <strong>{count}</strong>
                        </li>
                      ))}
                  </>
                ) : (
                  <li className="text-sm text-gray-500 p-1">No hay carros con información.</li>
                )}
              </ul>
            </details>
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">          
          {['todas', 'en-proceso', 'enlace-wa', 'mercately'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
            >
              {tab.replace('en-proceso', 'En Proceso (Clarenta)').replace('enlace-wa', 'Enlace WA').replace('todas', 'Todas')}
            </button>
          ))}
        </nav>
      </div>
      <div className="mt-4">{renderTabContent(activeTab)}</div>
    </div>
  );
};

export default ProspectsReport;
